import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/providers';
import type { Board, BoardQuery } from '../../lib/api';

export const qk = {
  orgs: ['orgs'] as const,
  projects: (orgId: string, trashed = false) => ['projects', orgId, trashed] as const,
  project: (id: string) => ['project', id] as const,
  inbox: (orgId: string) => ['inbox', orgId] as const,
  folders: (projectId: string) => ['folders', projectId] as const,
  boards: (q: BoardQuery) => ['boards', q.orgId, q.projectId ?? null, Boolean(q.trashed)] as const,
  templates: (orgId: string) => ['templates', orgId] as const,
  board: (id: string) => ['board', id] as const,
  boardPeople: (id: string) => ['board', id, 'people'] as const,
  recents: ['recents'] as const,
  favorites: ['favorites'] as const,
  search: (q: string) => ['search', q] as const,
  members: (orgId: string) => ['members', orgId] as const,
  profile: ['profile'] as const,
  storageReport: (orgId: string) => ['storageReport', orgId] as const,
};

/** P2 has one studio per user in practice: the first organization is the current one. */
export function useOrg() {
  const api = useApi();
  const query = useQuery({ queryKey: qk.orgs, queryFn: () => api.myOrgs() });
  return { ...query, org: query.data?.[0] ?? null };
}

export function useProjects(orgId: string | undefined, trashed = false) {
  const api = useApi();
  return useQuery({ queryKey: qk.projects(orgId ?? '', trashed), queryFn: () => api.listProjects(orgId!, { trashed }), enabled: Boolean(orgId) });
}

/**
 * The organization's « Sans projet » project (P17): its id, the server creating it when missing. It is a dashboard
 * section and a destination for boards, never an entry of the project lists. Members only; the id never changes.
 */
export function useInbox(orgId: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.inbox(orgId ?? ''), queryFn: () => api.orgInbox(orgId!), enabled: Boolean(orgId), staleTime: Infinity });
}

/** The stored name of the system project: what the server answers where there is a name and no project id. */
export const INBOX_NAME = 'Sans projet';

/** The projects of a picker with « Sans projet » first, once its id is known (outside the pickers' own sort). */
export function withInbox<P extends { id: string; name: string }>(inboxId: string | undefined, label: string, projects: readonly P[]): { id: string; name: string }[] {
  const rest = projects.map((p) => ({ id: p.id, name: p.name }));
  return inboxId ? [{ id: inboxId, name: label }, ...rest] : rest;
}

export function useProject(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.project(id), queryFn: () => api.getProject(id) });
}

export function useFolders(projectId: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.folders(projectId), queryFn: () => api.listFolders(projectId) });
}

/** The organization's custom templates (P10). */
export function useTemplates(orgId: string | null) {
  const api = useApi();
  return useQuery({ queryKey: qk.templates(orgId ?? ''), queryFn: () => api.listTemplates(orgId!), enabled: Boolean(orgId) });
}

export function useBoards(query: BoardQuery | null) {
  const api = useApi();
  return useQuery({
    queryKey: qk.boards(query ?? { orgId: '' }),
    queryFn: () => api.listBoards(query!),
    enabled: Boolean(query?.orgId),
  });
}

export function useRecents(limit?: number) {
  const api = useApi();
  return useQuery({ queryKey: limit ? [...qk.recents, limit] : qk.recents, queryFn: () => api.listRecents(limit) });
}

/** The organization's members, for owner names in list views. */
export function useMembers(orgId: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.members(orgId ?? ''), queryFn: () => api.listMembers(orgId!), enabled: Boolean(orgId) });
}

export function useFavorites() {
  const api = useApi();
  return useQuery({ queryKey: qk.favorites, queryFn: () => api.listFavorites() });
}

export function useSearch(q: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.search(q), queryFn: () => api.searchTitles(q), enabled: q.trim().length > 0 });
}

/** Optimistic favorite toggle: the star flips at once and rolls back if the server refuses. */
export function useToggleFavorite() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ board, on }: { board: Board; on: boolean }) => api.setFavorite(board.id, on),
    onMutate: async ({ board, on }) => {
      await queryClient.cancelQueries({ queryKey: qk.favorites });
      const previous = queryClient.getQueryData<Board[]>(qk.favorites);
      queryClient.setQueryData<Board[]>(qk.favorites, (list = []) =>
        on ? [board, ...list.filter((b) => b.id !== board.id)] : list.filter((b) => b.id !== board.id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => queryClient.setQueryData(qk.favorites, context?.previous),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.favorites }),
  });
}

/** Everything that lists boards or projects is refreshed after a write. */
export function useInvalidateContent() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      [['projects'], ['project'], ['folders'], ['boards'], ['board'], ['templates'], qk.recents, qk.favorites, ['search'], ['storageReport'], ['storage']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
}
